import { derived, writable } from "svelte/store";
import { vec2, mat3 } from "gl-matrix";

const PAN_DECAY_TAU = 0.35; // seconds; fling momentum decay
const PAN_ACCEL_TAU = 0.22; // seconds; keyboard-pan ramp up and down
const ZOOM_EASE_TAU = 0.12; // seconds; smooth wheel-zoom easing
const MIN_PAN_SPEED = 0.05; // viewport-fractions/sec; stop fling below this
const MIN_ZOOM_REMAINING = 1e-3;
const MIN_ZOOM_SCALE = 0.05; // smallest view scale = max zoom-in depth
const MAX_ZOOM_SCALE = 5.0; // largest view scale = max zoom-out
const ORIGIN = vec2.fromValues(0, 0);

function createView() {
  const { subscribe, update } = writable(mat3.create());

  let previousFit = mat3.create(); // the last resize's contain-fit transform

  let flingVelocity = null; // vec2, viewport-fractions/sec
  let panTarget = null; // vec2, velocity the held arrow keys ask for; null when none held
  let panVelocity = vec2.create(); // vec2, current pan velocity, eased toward panTarget
  let zoomRemaining = 0; // log-space zoom left to apply (smooth wheel)
  let zoomCenter = null; // vec2
  let lastTime = null;

  function translate(vec) {
    update((mat) => {
      const clone = mat3.clone(mat);
      mat3.translate(clone, clone, vec);
      return clone;
    });
  }

  function zoom(scalar, center) {
    update((mat) => {
      scalar = Math.max(scalar, Math.min(1.0, MIN_ZOOM_SCALE / mat[0]));
      scalar = Math.min(scalar, Math.max(1.0, MAX_ZOOM_SCALE / mat[0]));
      const clone = mat3.clone(mat);
      const scale = vec2.fromValues(scalar, scalar);
      const negCenter = vec2.negate(vec2.create(), center);
      mat3.translate(clone, clone, center);
      mat3.scale(clone, clone, scale);
      mat3.translate(clone, clone, negCenter);
      return clone;
    });
  }

  // The default view: scale the screen so the unit-square bordered region is
  // centered and fully contained, letterboxing the longer canvas axis. Maps
  // screen-uv -> map-uv as a scale about the center (0.5, 0.5).
  function fit(width, height) {
    const aspect = width / height;
    const sx = Math.max(1, aspect); // wider canvas -> reveal more map horizontally
    const sy = Math.max(1, 1 / aspect); // taller canvas -> reveal more vertically
    return mat3.fromValues(sx, 0, 0, 0, sy, 0, 0.5 * (1 - sx), 0.5 * (1 - sy), 1);
  }

  function resize(width, height) {
    update((mat) => {
      const next = fit(width, height);
      // Re-fit around the user's pan/zoom: strip the old fit and apply the new
      // one (next * prevFit^-1 * mat), so the map stays centered and correctly
      // scaled across resizes instead of stretching with the canvas.
      const out = mat3.invert(mat3.create(), previousFit);
      mat3.multiply(out, next, out);
      mat3.multiply(out, out, mat);
      previousFit = next;
      return out;
    });
  }

  // Stepped once per frame by the Canvas render loop, so the view, the WebGL
  // draw, and the DOM overlays all settle on the same transform each frame.
  function advance(now) {
    const dt = lastTime === null ? 0 : Math.min((now - lastTime) / 1000, 0.1);
    lastTime = now;
    if (dt <= 0) {
      return;
    }

    if (zoomCenter && Math.abs(zoomRemaining) > MIN_ZOOM_REMAINING) {
      const ease = 1 - Math.exp(-dt / ZOOM_EASE_TAU);
      const applied = zoomRemaining * ease;
      zoom(Math.exp(applied), zoomCenter);
      zoomRemaining -= applied;
    } else {
      zoomRemaining = 0;
    }

    if (flingVelocity) {
      const stepDist = vec2.scale(vec2.create(), flingVelocity, dt);
      translate(vec2.negate(vec2.create(), stepDist));
      vec2.scale(flingVelocity, flingVelocity, Math.exp(-dt / PAN_DECAY_TAU));
      if (vec2.length(flingVelocity) < MIN_PAN_SPEED) {
        flingVelocity = null;
      }
    }

    // Ease the pan velocity toward the held-key target, so panning accelerates
    // into motion and glides to a stop rather than snapping on and off.
    if (panTarget || vec2.length(panVelocity) >= MIN_PAN_SPEED) {
      const ease = 1 - Math.exp(-dt / PAN_ACCEL_TAU);
      vec2.lerp(panVelocity, panVelocity, panTarget || ORIGIN, ease);
      translate(vec2.scale(vec2.create(), panVelocity, dt));
    } else {
      vec2.set(panVelocity, 0, 0);
    }
  }

  return {
    subscribe,
    translate,
    zoom,
    resize,
    advance,
    fling(velocity) {
      flingVelocity = vec2.clone(velocity);
    },
    smoothZoom(logFactor, center) {
      zoomRemaining += logFactor;
      zoomCenter = vec2.clone(center);
    },
    // Sets the target velocity (viewport-fractions/sec) the held arrow keys ask
    // for; advance eases the actual velocity toward it. Zero clears the target.
    pan(velocity) {
      panTarget =
        velocity && (velocity[0] !== 0 || velocity[1] !== 0)
          ? vec2.clone(velocity)
          : null;
    },
    stop() {
      flingVelocity = null;
      panTarget = null;
      vec2.set(panVelocity, 0, 0);
      zoomRemaining = 0;
      zoomCenter = null;
    },
    reset() {
      flingVelocity = null;
      panTarget = null;
      vec2.set(panVelocity, 0, 0);
      zoomRemaining = 0;
      zoomCenter = null;
      update(() => mat3.clone(previousFit));
    },
  };
}

export const view = createView();
export const invView = derived(view, ($view) => {
  let clone = mat3.clone($view);
  mat3.invert(clone, clone);
  return clone;
});
